import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { SEEDH } from "@/lib/seedh";

const links = [
    { to: "/about", label: "About" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/tracker", label: "Tracker" },
    { to: "/donate", label: "Donate" },
    { to: "/gallery", label: "Gallery" },
    { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.hash]);

    return (
        <motion.header
            data-testid="navbar"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-seedh-border shadow-[0_8px_30px_-20px_rgba(4,88,134,0.25)]"
                    : "bg-white/65 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center shrink-0"
                >
                    <img
                        src={SEEDH.logoLight}
                        alt={`${SEEDH.name} — ${SEEDH.tagline}`}
                        className="h-12 md:h-16 w-auto object-contain transition-transform duration-500 hover:scale-105"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8 xl:gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.label}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `link-underline text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                                    isActive
                                        ? "text-seedh-blue"
                                        : "text-seedh-text hover:text-seedh-blue"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3 shrink-0">
                    <Link
                        to="/donate"
                        data-testid="nav-donate-cta"
                        className="btn-primary rounded-full px-5 xl:px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2 group whitespace-nowrap"
                    >
                        <Heart className="w-4 h-4 transition-transform group-hover:scale-110" />
                        Donate Now
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden w-11 h-11 rounded-full border border-seedh-border bg-white flex items-center justify-center text-seedh-blue shrink-0"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-white border-t border-seedh-border"
                    >
                        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-1">
                            {links.map((l, i) => (
                                <motion.div
                                    key={l.label}
                                    initial={{ x: -8, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.04 * i }}
                                >
                                    <Link
                                        to={l.to}
                                        className="block py-3 text-base text-seedh-text border-b border-seedh-border/60"
                                    >
                                        {l.label}
                                    </Link>
                                </motion.div>
                            ))}
                            <Link
                                to="/donate"
                                className="btn-primary rounded-full px-6 py-3 text-sm font-semibold text-center mt-4 inline-flex items-center justify-center gap-2"
                            >
                                <Heart className="w-4 h-4" />
                                Donate Now
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
